/**
 * Laptop fleet overview (release 2.1 item 16).
 *
 * Device-state tiles in the one fleet vocabulary (release 3.1: Healthy,
 * Degraded, Silent, Down, Never checked in - the same words and thresholds as
 * Edge Fleet), scan coverage in the last 24 h, the ten riskiest laptops, the
 * ten most common data types and a 30-day trend. Everything comes from ONE
 * summary call and one trend call, both built from the rollups and the laptop
 * rows: never from a page of findings.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, Clock, HelpCircle, Laptop, RefreshCw, WifiOff } from 'lucide-react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import { ErrorState, LoadingState } from '@foctta/ui-shell';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { KpiCard } from '@/components/viz/KpiCard';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { cn } from '@/lib/cn';
import { listBusinessUnits } from '@/lib/api';
import {
  getFleetSummary,
  getFleetTrend,
  getInventoryTree,
  type FleetSummary,
  type TrendPoint,
} from '@/lib/fleet-api';

import { FreshnessNote } from './FreshnessNote';
import { useLatestGuard } from './use-latest';
import {
  LIVENESS_LABEL,
  SEVERITY_DOT,
  SEVERITY_TEXT,
  ago,
  fmtCount,
  laptopStateCounts,
  plural,
  severityOfRank,
  sharedLabelNote,
} from './fleet-format';

interface BuOption {
  value: string;
  label: string;
}

/** Business units for the filter: the BU list when the reader may see it, else the BUs that hold laptop findings. */
async function loadBuOptions(): Promise<BuOption[]> {
  try {
    const r = await listBusinessUnits({ status: 'active' });
    return [
      ...r.businessUnits.map((b) => ({ value: b.id, label: b.name })),
      { value: 'unassigned', label: 'Unassigned' },
    ];
  } catch {
    const t = await getInventoryTree({ node: 'fleet', onlyOpen: false });
    return t.items.map((i) => ({ value: i.businessUnitId ?? 'unassigned', label: i.label }));
  }
}

function TrendChart({ days }: { days: TrendPoint[] }) {
  const data = useMemo(
    () =>
      days.map((d) => ({
        day: d.day.slice(5),
        coverage: d.enrolled > 0 ? Math.round(((d.scanned24h + d.scannedWithSkips24h) / d.enrolled) * 1000) / 10 : null,
        silent: d.silent + d.stale,
        openFiles: d.openFiles,
      })),
    [days],
  );
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis dataKey="day" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
        <YAxis yAxisId="pct" domain={[0, 100]} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} unit="%" />
        <YAxis yAxisId="n" orientation="right" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
        <RTooltip
          contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', fontSize: 11 }}
          labelStyle={{ color: 'hsl(var(--foreground))' }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line yAxisId="pct" type="monotone" dataKey="coverage" name="Scanned in 24 h (%)" stroke="hsl(var(--success))" dot={false} strokeWidth={2} />
        <Line yAxisId="n" type="monotone" dataKey="openFiles" name="Files with open findings" stroke="hsl(var(--chart-1))" dot={false} strokeWidth={2} />
        <Line yAxisId="n" type="monotone" dataKey="silent" name="Offline or inactive laptops" stroke="hsl(var(--chart-7))" dot={false} strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function FleetOverview() {
  const [bu, setBu] = useState<string>('');
  const [buOptions, setBuOptions] = useState<BuOption[]>([]);
  const [summary, setSummary] = useState<FleetSummary | null>(null);
  const [trend, setTrend] = useState<TrendPoint[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trendError, setTrendError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const begin = useLatestGuard();
  const refresh = useCallback(async () => {
    const current = begin();
    setLoading(true);
    setError(null);
    setTrendError(null);
    const filter = bu ? { bu } : {};
    const [s, t] = await Promise.allSettled([getFleetSummary(filter), getFleetTrend({ ...filter, days: 30 })]);
    // A reload for another business unit (or the 60 s refresh) started since.
    if (!current()) return;
    if (s.status === 'fulfilled') setSummary(s.value);
    else setError(s.reason instanceof Error ? s.reason.message : 'Could not load the fleet summary');
    if (t.status === 'fulfilled') setTrend(t.value.days);
    else setTrendError(t.reason instanceof Error ? t.reason.message : 'Could not load the trend');
    setLoading(false);
  }, [bu, begin]);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), 60_000);
    return () => clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    void loadBuOptions().then(setBuOptions).catch(() => setBuOptions([]));
  }, []);

  const s = summary;
  const noLaptops = s !== null && s.laptops.enrolled === 0 && s.findings.devicesWithFindings === 0;
  const states = s ? laptopStateCounts(s.laptops) : null;

  return (
    <PageContainer>
      <PageHeader
        title="Endpoint Agents"
        subtitle="Status of the endpoint agent on every enrolled laptop: which are online, which were scanned in the last day, and which hold the most sensitive files. The files themselves are in Endpoint Data."
        badges={[
          ...(loading ? [{ label: 'Loading', variant: 'glass' as const }] : []),
          ...(error ? [{ label: 'Load error', variant: 'warning' as const }] : []),
        ]}
        actions={
          <div className="flex items-center gap-2">
            <select
              aria-label="Business unit"
              value={bu}
              onChange={(e) => setBu(e.target.value)}
              className="glass-lite text-xs rounded px-2 py-1 bg-background/40 border border-border/40"
            >
              <option value="">All business units you can see</option>
              {buOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
              <RefreshCw className={cn('h-3.5 w-3.5 mr-1', loading && 'animate-spin')} /> Refresh
            </Button>
          </div>
        }
      />

      {error && <ErrorState title="Could not load the fleet summary" detail={error} onRetry={() => void refresh()} />}
      {!error && !s && <LoadingState caption="Adding up the fleet..." />}

      {noLaptops && (
        <GlassCard className="p-8 text-center">
          <Laptop className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <div className="text-sm font-medium">No laptops yet</div>
          <p className="mt-1 text-xs text-muted-foreground">
            Laptops appear here after the endpoint agent is installed and checks in. Start from{' '}
            <Link to="/admin/edge-provisioning" className="text-primary hover:underline">Deploy agent</Link>.
          </p>
        </GlassCard>
      )}

      {s && !noLaptops && (
        <>
          <FreshnessNote freshness={s.freshness} className="mb-3" />

          {states && (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
              <KpiCard label="Enrolled laptops" value={s.laptops.enrolled} icon={Laptop} hint="installed and not retired" />
              <KpiCard
                label={states.split ? 'Healthy' : LIVENESS_LABEL.reporting}
                value={states.healthy}
                icon={CheckCircle2}
                hint={states.split ? 'checked in within 24 h, no fault' : 'checked in within 24 h'}
              />
              <KpiCard
                label="Unhealthy"
                value={states.degraded ?? 0}
                icon={AlertTriangle}
                glow={states.degraded ? 'medium' : undefined}
                hint="online, but not working (see the reason in Agent Management)"
                unavailable={states.degraded === null ? 'not reported' : undefined}
              />
              <KpiCard label={LIVENESS_LABEL.silent} value={states.silent} icon={Clock} glow={states.silent > 0 ? 'medium' : undefined} hint="last check-in 1 to 7 days ago" />
              <KpiCard label={LIVENESS_LABEL.stale} value={states.down} icon={WifiOff} glow={states.down > 0 ? 'high' : undefined} hint="no check-in for over 7 days" />
              <KpiCard label={LIVENESS_LABEL.never_reported} value={states.pending} icon={HelpCircle} hint="enrolled, never checked in" />
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <GlassCard className="p-5">
              <CardHeader className="p-0 mb-2">
                <CardTitle className="text-sm">Scanned in the last 24 h</CardTitle>
              </CardHeader>
              <div className="text-3xl font-semibold tabular-nums">
                {fmtCount(s.coverage.scanned)} <span className="text-base font-normal text-muted-foreground">of {fmtCount(s.coverage.laptops)}</span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {s.coverage.coveragePct !== null ? `${s.coverage.coveragePct}% of enrolled laptops` : 'No laptops enrolled'}
                {s.coverage.scannedWithSkips > 0 ? `; ${fmtCount(s.coverage.scannedWithSkips)} did not read some files` : ''}
                {s.coverage.failed > 0 ? `; ${plural(s.coverage.failed, 'failed scan')}` : ''}.
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-success" style={{ width: `${s.coverage.coveragePct ?? 0}%` }} />
              </div>
              <Link to="/dspm/scans" className="mt-3 inline-block text-xs text-primary hover:underline">
                See which laptops were not scanned
              </Link>
            </GlassCard>

            <GlassCard className="p-5 md:col-span-2">
              <CardHeader className="p-0 mb-2">
                <CardTitle className="text-sm">Sensitive files on laptops</CardTitle>
                <CardDescription className="text-[11px]">{s.openDefinition}</CardDescription>
              </CardHeader>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <div className="text-2xl font-semibold tabular-nums">{fmtCount(s.findings.openFiles)}</div>
                  <div className="text-[11px] text-muted-foreground">files with open findings</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold tabular-nums">{fmtCount(s.findings.openFindings)}</div>
                  <div className="text-[11px] text-muted-foreground">open findings</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold tabular-nums">{fmtCount(s.findings.devicesWithOpenFindings)}</div>
                  <div className="text-[11px] text-muted-foreground">laptops holding them</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold tabular-nums text-sev-critical">{fmtCount(s.findings.filesByWorst.critical)}</div>
                  <div className="text-[11px] text-muted-foreground">files with a critical finding</div>
                </div>
              </div>
              {(s.findings.weakOpen > 0 || s.findings.reviewOpen > 0) && (
                <div className="mt-3 text-[11px] text-muted-foreground">
                  Not counted above: {fmtCount(s.findings.weakOpen)} weak matches and {fmtCount(s.findings.reviewOpen)} findings
                  waiting for review.
                </div>
              )}
            </GlassCard>
          </div>

          <div className="grid gap-4 lg:grid-cols-2 mb-6">
            <GlassCard className="p-5">
              <CardHeader className="p-0 mb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-sev-high" /> Ten riskiest laptops
                </CardTitle>
                <CardDescription className="text-[11px]" title={s.riskFormula}>
                  Risk score: {s.riskFormula}
                </CardDescription>
              </CardHeader>
              {s.topDevices.length === 0 ? (
                <div className="glass-lite rounded-md p-4 text-center text-xs text-muted-foreground">
                  No laptop has an open finding.
                </div>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="pb-1 pr-2 font-medium">Laptop</th>
                      <th className="pb-1 pr-2 font-medium">Data types</th>
                      <th className="pb-1 pr-2 font-medium text-right">Files</th>
                      <th className="pb-1 font-medium text-right">Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.topDevices.map((d) => {
                      const sev = severityOfRank(d.maxSeverity);
                      const why = d.riskExplain.map((e) => `${e.dataType} (${e.severity}, ${e.files} files): ${e.points}`).join('\n');
                      const shared = sharedLabelNote(d.pod?.pods);
                      return (
                        <tr key={`${d.deviceKey}|${d.businessUnitId ?? ''}`} className="border-t border-border/30 align-top">
                          <td className="py-1.5 pr-2">
                            <div className="flex items-center gap-1.5 text-xs font-medium">
                              {sev && <span className={cn('h-2 w-2 rounded-full shrink-0', SEVERITY_DOT[sev])} />}
                              {d.deviceKey}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                              {d.businessUnitId === null ? 'Unassigned' : d.businessUnitName ?? 'Business unit not visible'}
                              {d.pod ? ` · ${d.pod.liveness ? LIVENESS_LABEL[d.pod.liveness] : 'no check-in'}, checked in ${ago(d.pod.lastHeartbeatAt)}` : ' · no enrolled laptop with this name'}
                            </div>
                            {shared && <div className="text-[11px] text-warning">{shared}</div>}
                          </td>
                          <td className="py-1.5 pr-2 text-[11px] text-muted-foreground">{d.topTypes.join(', ')}</td>
                          <td className="py-1.5 pr-2 text-xs text-right tabular-nums">{fmtCount(d.openFiles)}</td>
                          <td className={cn('py-1.5 text-xs text-right tabular-nums font-medium', sev ? SEVERITY_TEXT[sev] : '')} title={why}>
                            {d.riskScore.toFixed(1)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              <Link to="/dspm/laptops" className="mt-3 inline-block text-xs text-primary hover:underline">
                Open the laptop inventory
              </Link>
            </GlassCard>

            <GlassCard className="p-5">
              <CardHeader className="p-0 mb-2">
                <CardTitle className="text-sm">Ten most common data types</CardTitle>
                <CardDescription className="text-[11px]">
                  A file with three data types counts once under each, so these do not add up to the file total.
                </CardDescription>
              </CardHeader>
              {s.topTypes.length === 0 ? (
                <div className="glass-lite rounded-md p-4 text-center text-xs text-muted-foreground">No data types found on laptops.</div>
              ) : (
                <div className="space-y-1.5">
                  {s.topTypes.map((t) => {
                    const max = s.topTypes[0]!.openFiles || 1;
                    const sev = severityOfRank(t.maxSeverity);
                    return (
                      <div key={t.dataType}>
                        <div className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1.5">
                            {sev && <span className={cn('h-2 w-2 rounded-full', SEVERITY_DOT[sev])} />}
                            {t.dataType.replace(/_/g, ' ')}
                          </span>
                          <span className="text-muted-foreground tabular-nums">
                            {plural(t.openFiles, 'file')} on {plural(t.devices, 'laptop')}
                          </span>
                        </div>
                        <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                          <div className="h-full bg-chart-1" style={{ width: `${(t.openFiles / max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </GlassCard>
          </div>

          <GlassCard className="p-5">
            <CardHeader className="p-0 mb-2">
              <CardTitle className="text-sm">Last 30 days</CardTitle>
              <CardDescription clamp className="text-[11px]">
                One point per day (UTC), taken from the daily snapshot; today&apos;s point refreshes every hour.
              </CardDescription>
            </CardHeader>
            {trendError && <ErrorState title="Could not load the trend" detail={trendError} onRetry={() => void refresh()} />}
            {!trendError && trend === null && <LoadingState caption="Loading the trend..." />}
            {!trendError && trend !== null && trend.length < 2 && (
              <div className="glass-lite rounded-md p-4 text-center text-xs text-muted-foreground">
                The trend starts once daily snapshots build up: {plural(trend.length, 'day')} recorded so far.
              </div>
            )}
            {!trendError && trend !== null && trend.length >= 2 && <TrendChart days={trend} />}
          </GlassCard>
        </>
      )}
    </PageContainer>
  );
}
